import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import * as api from '../api/invoice-imports';

export const useInvoiceImportsPaginated = (params) =>
  useInfiniteQuery({
    queryKey: ['invoice-imports', 'paginated', params],
    queryFn: ({ pageParam = 1 }) =>
      api.getInvoiceImportsPaginated({ ...params, page: pageParam }),
    getNextPageParam: (lastPage) =>
      lastPage.page < lastPage.totalPages ? lastPage.page + 1 : undefined,
    initialPageParam: 1,
    placeholderData: (prev) => prev,
  });

export const useInvoiceImport = (uuid) =>
  useQuery({
    queryKey: ['invoice-imports', uuid],
    queryFn: () => api.getInvoiceImport(uuid),
    enabled: !!uuid,
    refetchInterval: (query) =>
      query.state.data?.status === 'processing' ? 2500 : false,
  });

export const useUploadInvoiceImport = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (formData) => api.uploadInvoiceImport(formData),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['invoice-imports'] }),
  });
};

export const useUpdateInvoiceImport = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ uuid, data }) => api.updateInvoiceImport(uuid, data),
    onSuccess: (result) => {
      qc.setQueryData(['invoice-imports', result.uuid], result);
      qc.invalidateQueries({ queryKey: ['invoice-imports', 'paginated'] });
    },
  });
};

export const useConfirmInvoiceImport = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ uuid, data }) => api.confirmInvoiceImport(uuid, data),
    onSuccess: (result) => {
      qc.setQueryData(['invoice-imports', result.uuid], result);
      qc.invalidateQueries({ queryKey: ['invoice-imports'] });
      qc.invalidateQueries({ queryKey: ['purchase-invoices'] });
    },
  });
};

export const useRetryInvoiceImport = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ uuid, businessUuid }) =>
      api.retryInvoiceImport(uuid, businessUuid),
    onSuccess: (result) => {
      qc.setQueryData(['invoice-imports', result.uuid], result);
      qc.invalidateQueries({ queryKey: ['invoice-imports', 'paginated'] });
    },
  });
};

export const useDeleteInvoiceImport = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ uuid, businessUuid }) =>
      api.deleteInvoiceImport(uuid, businessUuid),
    onSuccess: (_, { uuid }) => {
      qc.removeQueries({ queryKey: ['invoice-imports', uuid] });
      qc.invalidateQueries({ queryKey: ['invoice-imports'], exact: false });
    },
  });
};
